import { useCallback, useMemo, useState } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { useStore } from 'react-redux';
import { useAvatarContext } from '@avatar/context/AvatarProvider';
import { useAppSelector } from 'store/hooks';
import { AppState } from 'store';
import { buildSectionView, SectionViewOptions } from '../Sections/selectors';
import useSectionViewOptions from '../Sections/hooks/useSectionViewOptions';
import { Section } from '../../../../server/graphql/__generated.types';
import {
  SectionsDocument,
  SectionsQuery,
  useSectionsQuery,
} from '../Sections/operation.generated';

export interface SectionSearchDocument {
  id: string;
  index: number;
  avatarName: string;
  sectionName: string;
  plainText: string;
}

/**
 * @description builds a fuse.js search document for project sections (avatar name,
 * section name, plain text)
 */
export const buildSectionsSearchDocument = (
  sections: Section[],
  draftsById: AppState['scriptStudioBuffers']['byId'],
  queryAvatar: ReturnType<typeof useAvatarContext>['queryAvatar'],
  options: SectionViewOptions = {}
): SectionSearchDocument[] =>
  sections.map((section, index) => {
    const sectionView = buildSectionView(
      section,
      draftsById[section.id],
      undefined,
      undefined,
      options
    );
    return {
      id: section.id,
      index,
      avatarName:
        queryAvatar({
          avatarId: sectionView.avatar,
          variantId: sectionView.avatarVariant,
        })?.avatar?.name ?? '',
      sectionName: section.name,
      plainText: sectionView.text,
    };
  });

/**
 * @description reads the sections connection straight out of the
 * Apollo cache without subscribing
 */
export const readCachedSections = (
  apolloClient: ApolloClient<object>,
  projectId: string
): Section[] => {
  const cached = apolloClient.readQuery<SectionsQuery>({
    query: SectionsDocument,
    variables: { inputs: { projectId } },
  });
  return cached?.sections.edges.map(edge => edge.node) ?? [];
};

/**
 * @description reads sections (Apollo cache) and drafts (Redux store)
 * unreactively via the returned callback - intended to be called solely on
 * search bar focus event handler
 */
const useSectionsSearchDocument = (projectId: string) => {
  const [sectionsSearchDocument, setSectionsSearchDocument] = useState<
    SectionSearchDocument[]
  >([]);
  const apolloClient = useApolloClient();
  const store = useStore<AppState>();
  const { queryAvatar } = useAvatarContext();
  const sectionViewOptions = useSectionViewOptions();

  const refreshSectionsSearchDocument = useCallback(() => {
    const sections = readCachedSections(apolloClient, projectId);
    const draftsById = store.getState().scriptStudioBuffers.byId;
    const documents = buildSectionsSearchDocument(
      sections,
      draftsById,
      queryAvatar,
      sectionViewOptions
    );
    setSectionsSearchDocument(documents);
  }, [apolloClient, store, queryAvatar, projectId, sectionViewOptions]);

  return { sectionsSearchDocument, refreshSectionsSearchDocument };
};

/**
 * @description the same document as `useSectionsSearchDocument`, but rebuilt
 * whenever the sections query or the draft buffers change.
 *
 * This is what makes the results count able to stay truthful while the visible
 * results stay frozen. It is only built while a search is active - there is
 * nothing to keep a count of otherwise.
 */
export const useLiveSectionsSearchDocument = (
  projectId: string,
  isSearchActive: boolean
): SectionSearchDocument[] => {
  const { data } = useSectionsQuery({
    variables: { inputs: { projectId } },
    skip: !isSearchActive,
    fetchPolicy: 'cache-only',
  });
  const draftsById = useAppSelector(state => state.scriptStudioBuffers.byId);
  const { queryAvatar } = useAvatarContext();
  const sectionViewOptions = useSectionViewOptions();

  return useMemo(() => {
    if (!isSearchActive) return [];
    const sections = data?.sections.edges.map(edge => edge.node) ?? [];
    return buildSectionsSearchDocument(
      sections,
      draftsById,
      queryAvatar,
      sectionViewOptions
    );
  }, [isSearchActive, data, draftsById, queryAvatar, sectionViewOptions]);
};

export default useSectionsSearchDocument;
